import {
  ActivityKindEnum,
  AdFieldBathsEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
} from '../../../types';
import type { ClientRequestModel } from '../../../types';

import type {
  AppliedAdsFilterLocationSnapshot,
  AppliedAdsFilterSnapshot,
} from './appliedAdsFilterSnapshot';

type ClientRequestPrefillOptionalField =
  | 'areaFrom'
  | 'areaTo'
  | 'baths'
  | 'bedsArr'
  | 'completionStatus'
  | 'coordinatesLat'
  | 'coordinatesLong'
  | 'locationId'
  | 'nameEn'
  | 'period'
  | 'placeId'
  | 'priceFrom'
  | 'priceTo'
  | 'propertyKind'
  | 'addressEn';

export type ClientRequestPrefill = Pick<ClientRequestModel, 'typeOfActivity'> &
  Partial<Pick<ClientRequestModel, ClientRequestPrefillOptionalField>>;

export type ClientRequestAmbiguityReason =
  | 'incomplete-location'
  | 'multiple-values-for-scalar'
  | 'unsupported-combination';

export type ClientRequestAmbiguousField =
  | {
      field: 'propertyKind';
      reason: 'multiple-values-for-scalar';
      sourceValue: PropertyKindEnum[];
    }
  | {
      field: 'baths';
      reason: 'multiple-values-for-scalar';
      sourceValue: AdFieldBathsEnum[];
    }
  | {
      field: 'completionStatus';
      reason: 'unsupported-combination';
      sourceValue: CompletionStatusEnum[];
    }
  | {
      field: 'location';
      reason: 'incomplete-location' | 'multiple-values-for-scalar';
      sourceValue: {
        location?: AppliedAdsFilterLocationSnapshot;
        locationId?: string[];
      };
    };

export type ClientRequestUnmappedFilterCode =
  | 'creator'
  | 'downPayment'
  | 'floor'
  | 'floorsNumFrom'
  | 'floorsNumTo'
  | 'furniture'
  | 'handover'
  | 'isMyAgency'
  | 'keywords'
  | 'multiHandover'
  | 'myFriends'
  | 'occupancy'
  | 'postHandover'
  | 'renovation'
  | 'transactionType';

export interface ClientRequestUnmappedFilter {
  filter: ClientRequestUnmappedFilterCode;
  value: Exclude<AppliedAdsFilterSnapshot[ClientRequestUnmappedFilterCode], undefined>;
}

export interface ClientRequestPrefillResult {
  prefill: ClientRequestPrefill;
  ambiguousFields: ClientRequestAmbiguousField[];
  unmappedFilters: ClientRequestUnmappedFilter[];
}

const hasValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;
  if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
  return value !== undefined;
};

const cloneValue = <T>(value: T): T => {
  if (Array.isArray(value)) return [...value] as T;
  if (typeof value === 'object' && value !== null) return { ...value };
  return value;
};

const getLocationSourceValue = (
  snapshot: AppliedAdsFilterSnapshot
): Extract<ClientRequestAmbiguousField, { field: 'location' }>['sourceValue'] => ({
  ...(snapshot.locationId?.length ? { locationId: [...snapshot.locationId] } : {}),
  ...(snapshot.location ? { location: { ...snapshot.location } } : {}),
});

const normalizeLocationText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const normalizeLocationCoordinate = (value: unknown): string | undefined => {
  const normalizedValue = normalizeLocationText(value);

  return normalizedValue && Number.isFinite(Number(normalizedValue.replace(',', '.')))
    ? normalizedValue
    : undefined;
};

const addUnmappedFilter = (
  target: ClientRequestUnmappedFilter[],
  filter: ClientRequestUnmappedFilterCode,
  value: AppliedAdsFilterSnapshot[ClientRequestUnmappedFilterCode]
) => {
  if (!hasValue(value)) return;

  target.push({
    filter,
    value: cloneValue(value) as ClientRequestUnmappedFilter['value'],
  });
};

export const buildClientRequestPrefill = (
  snapshot: AppliedAdsFilterSnapshot
): ClientRequestPrefillResult => {
  const prefill: ClientRequestPrefill = {
    typeOfActivity: snapshot.activityKind,
  };
  const ambiguousFields: ClientRequestAmbiguousField[] = [];
  const unmappedFilters: ClientRequestUnmappedFilter[] = [];

  if (snapshot.propertyKind?.length === 1) {
    prefill.propertyKind = snapshot.propertyKind[0];
  } else if (snapshot.propertyKind && snapshot.propertyKind.length > 1) {
    ambiguousFields.push({
      field: 'propertyKind',
      reason: 'multiple-values-for-scalar',
      sourceValue: [...snapshot.propertyKind],
    });
  }

  if (snapshot.beds?.length) {
    prefill.bedsArr = [...snapshot.beds];
  }

  if (snapshot.baths?.length === 1) {
    prefill.baths = snapshot.baths[0];
  } else if (snapshot.baths && snapshot.baths.length > 1) {
    ambiguousFields.push({
      field: 'baths',
      reason: 'multiple-values-for-scalar',
      sourceValue: [...snapshot.baths],
    });
  }

  if (snapshot.priceMin !== undefined) prefill.priceFrom = snapshot.priceMin;
  if (snapshot.priceMax !== undefined) prefill.priceTo = snapshot.priceMax;
  if (snapshot.areaMin !== undefined) prefill.areaFrom = snapshot.areaMin;
  if (snapshot.areaMax !== undefined) prefill.areaTo = snapshot.areaMax;

  if (snapshot.activityKind === ActivityKindEnum.Sale) {
    const completionStatus = snapshot.completionStatus;

    if (completionStatus?.length === 1) {
      prefill.completionStatus = completionStatus[0];
    } else if (
      completionStatus?.length === 2 &&
      completionStatus.includes(CompletionStatusEnum.Ready) &&
      completionStatus.includes(CompletionStatusEnum.OffPlan)
    ) {
      prefill.completionStatus = CompletionStatusEnum.All;
    } else if (completionStatus && completionStatus.length > 1) {
      ambiguousFields.push({
        field: 'completionStatus',
        reason: 'unsupported-combination',
        sourceValue: [...completionStatus],
      });
    }
  }

  if (snapshot.activityKind === ActivityKindEnum.Rent && snapshot.rentFrequency !== undefined) {
    prefill.period = snapshot.rentFrequency;
  }

  const locationIds = (snapshot.locationId ?? [])
    .map((locationId) => normalizeLocationText(locationId))
    .filter((locationId): locationId is string => locationId !== undefined);
  const location = snapshot.location;
  const hasLocationSource = locationIds.length > 0 || location !== undefined;
  const addressEn = normalizeLocationText(location?.addressEn);
  const nameEn = normalizeLocationText(location?.nameEn);
  const displayText = addressEn ?? nameEn;
  const coordinatesLat = normalizeLocationCoordinate(location?.coordinatesLat);
  const coordinatesLong = normalizeLocationCoordinate(location?.coordinatesLong);
  const placeId = normalizeLocationText(location?.placeId);

  if (hasLocationSource) {
    if (displayText) prefill.addressEn = displayText;

    if (locationIds.length > 1) {
      ambiguousFields.push({
        field: 'location',
        reason: 'multiple-values-for-scalar',
        sourceValue: getLocationSourceValue(snapshot),
      });
    } else if (displayText && coordinatesLat && coordinatesLong) {
      prefill.coordinatesLat = coordinatesLat;
      prefill.coordinatesLong = coordinatesLong;
      if (locationIds.length === 1) prefill.locationId = locationIds[0];
      if (nameEn) prefill.nameEn = nameEn;
      if (placeId) prefill.placeId = placeId;
    } else {
      ambiguousFields.push({
        field: 'location',
        reason: 'incomplete-location',
        sourceValue: getLocationSourceValue(snapshot),
      });
    }
  }

  addUnmappedFilter(unmappedFilters, 'transactionType', snapshot.transactionType);
  addUnmappedFilter(unmappedFilters, 'furniture', snapshot.furniture);
  addUnmappedFilter(unmappedFilters, 'renovation', snapshot.renovation);
  addUnmappedFilter(unmappedFilters, 'occupancy', snapshot.occupancy);
  addUnmappedFilter(unmappedFilters, 'floor', snapshot.floor);
  addUnmappedFilter(unmappedFilters, 'floorsNumFrom', snapshot.floorsNumFrom);
  addUnmappedFilter(unmappedFilters, 'floorsNumTo', snapshot.floorsNumTo);
  addUnmappedFilter(unmappedFilters, 'handover', snapshot.handover);
  addUnmappedFilter(unmappedFilters, 'multiHandover', snapshot.multiHandover);
  addUnmappedFilter(unmappedFilters, 'postHandover', snapshot.postHandover);
  addUnmappedFilter(unmappedFilters, 'downPayment', snapshot.downPayment);
  addUnmappedFilter(unmappedFilters, 'creator', snapshot.creator);
  addUnmappedFilter(unmappedFilters, 'isMyAgency', snapshot.isMyAgency);
  addUnmappedFilter(unmappedFilters, 'myFriends', snapshot.myFriends);
  addUnmappedFilter(unmappedFilters, 'keywords', snapshot.keywords);

  return {
    prefill,
    ambiguousFields,
    unmappedFilters,
  };
};
